import {
  AppointmentListFiltersInput,
  AppointmentRecord,
  AppointmentRecordsByDoctorIDAndDateAndTimeQuery,
  AppointmentRecordsByLocationIDAndDateAndTimeQuery,
  AppointmentStatus,
  ListAppointmentsOptimizedInput,
  ModelAppointmentRecordByDoctorCompositeKeyConditionInput,
  ModelAppointmentRecordByLocationCompositeKeyConditionInput,
  ModelAppointmentRecordFilterInput,
  ModelSortDirection,
} from "~/api/API";
import {
  appointmentRecordsByDoctorIDAndDateAndTime,
  appointmentRecordsForSchedulerAssistant,
  appointmentRecordsForSchedulerAssistantByLocation,
  appointmentRecordsOptimized,
} from "~/api/graphql-custom/queries";
import { getFutureDateFromDateISO } from "~/common/utils";
import { DEFAULT_DATE_ISO } from "~/constants";
import { GraphQLClientWrapper, graphqlClient } from "~/lib/graphql-client";

export enum SortingDirection {
  ASC = "ASC",
  DESC = "DESC",
}

export enum SortingField {
  APPOINTMENT_DATE = "APPOINTMENT_DATE",
  INQUIRY_DATE = "INQUIRY_DATE",
  REPORT_DUE = "REPORT_DUE",
  HISTORY_CALL_DATE = "HISTORY_CALL_DATE",
}

export interface SortingOption {
  field: SortingField;
  direction: SortingDirection;
}

export class AppointmentListService {
  private static instance: AppointmentListService;
  private readonly client: GraphQLClientWrapper;

  private constructor(client: GraphQLClientWrapper) {
    this.client = client;
  }

  static getInstance(client?: GraphQLClientWrapper): AppointmentListService {
    if (!AppointmentListService.instance) {
      AppointmentListService.instance = new AppointmentListService(
        client || graphqlClient,
      );
    }
    return AppointmentListService.instance;
  }

  async listAppointmentsOptimized(
    isProd: boolean,
    sorting: SortingOption,
    startDate?: string,
    endDate?: string,
    includeEmptyDates?: boolean,
    inquiryStartDate?: string,
    inquiryEndDate?: string,
    nextToken?: string | null,
    filters?: AppointmentListFiltersInput,
    limit = 100,
    minResults = 20,
    isDemo?: boolean,
  ): Promise<{ appointments: AppointmentRecord[]; nextToken: string | null }> {
    const input: ListAppointmentsOptimizedInput = {
      isProd,
      isDemo,
      sortingField: sorting.field,
      sortingDirection: sorting.direction,
      startDate,
      endDate,
      includeEmptyDates,
      inquiryStartDate,
      inquiryEndDate,
      nextToken,
      limit,
      minResults,
      filters,
    };

    const response = await this.client.graphql(
      {
        query: appointmentRecordsOptimized,
        variables: { input },
      },
      { errorMode: "partial" },
    );

    const items =
      (response?.data?.listAppointmentsOptimized
        ?.appointments as AppointmentRecord[]) || [];
    const newToken = response?.data?.listAppointmentsOptimized?.nextToken;
    return { appointments: items, nextToken: newToken || null };
  }

  async listAppointmentsForDoctor(
    isProd: boolean,
    doctorID: string,
    sorting: SortingOption,
    startDate?: string,
    endDate?: string,
    includeEmptyDates?: boolean,
    inquiryStartDate?: string,
    inquiryEndDate?: string,
    nextToken?: string | null,
    isDemo?: boolean,
  ): Promise<{ appointments: AppointmentRecord[]; nextToken: string | null }> {
    // Build GraphQL variables from sorting, filter values, etc.
    const sortDirection =
      sorting.direction === SortingDirection.ASC
        ? ModelSortDirection.ASC
        : ModelSortDirection.DESC;

    const filter: ModelAppointmentRecordFilterInput = {
      active: { eq: true },
      and: [
        { status: { ne: AppointmentStatus.INQUIRY } },
        { status: { ne: AppointmentStatus.INQUIRY_CLOSED } },
        { status: { ne: AppointmentStatus.RESCHEDULING_HOLD } },
        { status: { ne: AppointmentStatus.LATE_RESCHEDULING_HOLD } },
        { status: { ne: AppointmentStatus.DEPOSITION_HOLD } },
        { status: { ne: AppointmentStatus.REEVAL_HOLD } },
      ],
      ...(isProd ? { isProd: { eq: true } } : { isProd: { ne: true } }),
      // Demo filter: demo users see only demo appointments, non-demo users see only non-demo
      ...(isDemo !== undefined
        ? isDemo
          ? { isDemo: { eq: true } }
          : { isDemo: { ne: true } }
        : {}),
    };

    const dateTimeCondition: ModelAppointmentRecordByDoctorCompositeKeyConditionInput =
      {};

    // add 1 day to endDate as exclusive boundary to include all appointments on the end date
    // this is necessary only for date#time composite key, as direct date comparsison will exclude
    // appointments on the end date
    const adjustedEndDate = endDate
      ? getFutureDateFromDateISO(endDate, 1)
      : null;

    if (startDate && adjustedEndDate) {
      dateTimeCondition.between = [
        { date: startDate },
        { date: adjustedEndDate },
      ];
    } else if (startDate) {
      dateTimeCondition.ge = { date: startDate };
    } else if (adjustedEndDate) {
      // exclusive boundary so we don't include appointments actually on the adjsuted end date
      dateTimeCondition.lt = { date: adjustedEndDate };
    }

    try {
      // REPORT_DUE has no server-side ordering: the byGlobalReportDue GSI
      // indexes only the sparse `reportDue` override (25 of ~7.7k prod rows),
      // so querying it would hide almost every appointment. Page on date#time
      // instead and let the client order the loaded window by the resolved due
      // date (`sortAppointmentsByGroup`). That is a proxy, not an equivalence:
      // it holds for the 30-day types, which derive from the appointment date,
      // but not for SUPPLEMENTAL (inquiryDate + 60). See
      // specs/report-due-derived-field.md.
      if (
        sorting.field === SortingField.APPOINTMENT_DATE ||
        sorting.field === SortingField.REPORT_DUE
      ) {
        if (includeEmptyDates) {
          delete dateTimeCondition.between;
          delete dateTimeCondition.ge;
          delete dateTimeCondition.lt;

          const orConditions = [];
          const dateFilter = getDateRangeFilter(startDate, endDate);
          if (Object.keys(dateFilter).length > 0) {
            orConditions.push({ date: dateFilter });
          }
          orConditions.push({ date: { eq: DEFAULT_DATE_ISO } });
          filter.or = orConditions;
        }
        if (inquiryStartDate || inquiryEndDate) {
          // Only add inquiryDate filter if dates are provided
          const inquiryDateFilter = getDateRangeFilter(
            inquiryStartDate,
            inquiryEndDate,
          );
          if (Object.keys(inquiryDateFilter).length > 0) {
            filter.inquiryDate = inquiryDateFilter;
          }
        }
        const response: {
          data: AppointmentRecordsByDoctorIDAndDateAndTimeQuery;
        } = await this.client.graphql({
          query: appointmentRecordsByDoctorIDAndDateAndTime,
          variables: {
            doctorID,
            sortDirection,
            filter: filter,
            nextToken: nextToken || null,
            ...(Object.keys(dateTimeCondition).length > 0
              ? { dateTime: dateTimeCondition }
              : {}),
            limit: 100,
          },
        });
        const items =
          (response?.data?.appointmentRecordsByDoctorIDAndDateAndTime
            ?.items as AppointmentRecord[]) || [];
        const newToken =
          response?.data?.appointmentRecordsByDoctorIDAndDateAndTime?.nextToken;
        return { appointments: items, nextToken: newToken || null };
      } else if (sorting.field === SortingField.INQUIRY_DATE) {
        // TODO: Support sorting by inquiry date for doctor appointments
        console.error(
          "Sorting by inquiry date not supported for doctor appointments",
        );
        return { appointments: [], nextToken: null };
      } else {
        return { appointments: [], nextToken: null };
      }
    } catch (err) {
      console.error("Error fetching appointments:", err);
      return { appointments: [], nextToken: null };
    }
  }

  async listAppointmentsForSchedulerAssistant(
    isProd: boolean,
    doctorID: string,
    sorting: SortingOption,
    startDate?: string,
    endDate?: string,
    includeEmptyDates?: boolean,
    nextToken?: string | null,
    isDemo?: boolean,
  ): Promise<{ appointments: AppointmentRecord[]; nextToken: string | null }> {
    const sortDirection =
      sorting.direction === SortingDirection.ASC
        ? ModelSortDirection.ASC
        : ModelSortDirection.DESC;

    const filter: ModelAppointmentRecordFilterInput = {
      active: { eq: true },
      // Inquiries are excluded entirely — their tentative date/location must
      // not surface in the scheduler assistant (grid or day summaries).
      and: [
        { status: { ne: AppointmentStatus.INQUIRY_CLOSED } },
        { status: { ne: AppointmentStatus.INQUIRY } },
      ],
      ...(isProd ? { isProd: { eq: true } } : { isProd: { ne: true } }),
      ...(isDemo !== undefined
        ? isDemo
          ? { isDemo: { eq: true } }
          : { isDemo: { ne: true } }
        : {}),
    };

    const dateTimeCondition: ModelAppointmentRecordByDoctorCompositeKeyConditionInput =
      {};

    const adjustedEndDate = endDate
      ? getFutureDateFromDateISO(endDate, 1)
      : null;

    if (startDate && adjustedEndDate) {
      dateTimeCondition.between = [
        { date: startDate },
        { date: adjustedEndDate },
      ];
    } else if (startDate) {
      dateTimeCondition.ge = { date: startDate };
    } else if (adjustedEndDate) {
      dateTimeCondition.lt = { date: adjustedEndDate };
    }

    try {
      if (includeEmptyDates) {
        delete dateTimeCondition.between;
        delete dateTimeCondition.ge;
        delete dateTimeCondition.lt;

        const orConditions = [];
        const dateFilter = getDateRangeFilter(startDate, endDate);
        if (Object.keys(dateFilter).length > 0) {
          orConditions.push({ date: dateFilter });
        }
        orConditions.push({ date: { eq: DEFAULT_DATE_ISO } });
        filter.or = orConditions;
      }

      const response: {
        data: AppointmentRecordsByDoctorIDAndDateAndTimeQuery;
      } = await this.client.graphql({
        query: appointmentRecordsForSchedulerAssistant,
        variables: {
          doctorID,
          sortDirection,
          filter: filter,
          nextToken: nextToken || null,
          ...(Object.keys(dateTimeCondition).length > 0
            ? { dateTime: dateTimeCondition }
            : {}),
          limit: 100,
        },
      });

      const items =
        (response?.data?.appointmentRecordsByDoctorIDAndDateAndTime
          ?.items as AppointmentRecord[]) || [];
      const newToken =
        response?.data?.appointmentRecordsByDoctorIDAndDateAndTime?.nextToken;

      return { appointments: items, nextToken: newToken || null };
    } catch (err) {
      console.error(
        "Error fetching appointments for scheduler assistant:",
        err,
      );
      return { appointments: [], nextToken: null };
    }
  }

  /**
   * List appointments for scheduler assistant by location.
   * Used to show appointments from other doctors at the preferred location.
   */
  async listAppointmentsForSchedulerAssistantByLocation(
    isProd: boolean,
    locationID: string,
    sorting: SortingOption,
    startDate?: string,
    endDate?: string,
    nextToken?: string | null,
    isDemo?: boolean,
  ): Promise<{ appointments: AppointmentRecord[]; nextToken: string | null }> {
    const sortDirection =
      sorting.direction === SortingDirection.ASC
        ? ModelSortDirection.ASC
        : ModelSortDirection.DESC;

    const filter: ModelAppointmentRecordFilterInput = {
      active: { eq: true },
      // Same inquiry exclusion as listAppointmentsForSchedulerAssistant, so
      // other doctors' inquiries don't render at a location either.
      and: [
        { status: { ne: AppointmentStatus.INQUIRY_CLOSED } },
        { status: { ne: AppointmentStatus.INQUIRY } },
      ],
      ...(isProd ? { isProd: { eq: true } } : { isProd: { ne: true } }),
      ...(isDemo !== undefined
        ? isDemo
          ? { isDemo: { eq: true } }
          : { isDemo: { ne: true } }
        : {}),
    };

    const dateTimeCondition: ModelAppointmentRecordByLocationCompositeKeyConditionInput =
      {};

    const adjustedEndDate = endDate
      ? getFutureDateFromDateISO(endDate, 1)
      : null;

    if (startDate && adjustedEndDate) {
      dateTimeCondition.between = [
        { date: startDate },
        { date: adjustedEndDate },
      ];
    } else if (startDate) {
      dateTimeCondition.ge = { date: startDate };
    } else if (adjustedEndDate) {
      dateTimeCondition.lt = { date: adjustedEndDate };
    }

    try {
      const response: {
        data: AppointmentRecordsByLocationIDAndDateAndTimeQuery;
      } = await this.client.graphql({
        query: appointmentRecordsForSchedulerAssistantByLocation,
        variables: {
          locationID,
          sortDirection,
          filter: filter,
          nextToken: nextToken || null,
          ...(Object.keys(dateTimeCondition).length > 0
            ? { dateTime: dateTimeCondition }
            : {}),
          limit: 100,
        },
      });

      const items =
        (response?.data?.appointmentRecordsByLocationIDAndDateAndTime
          ?.items as AppointmentRecord[]) || [];
      const newToken =
        response?.data?.appointmentRecordsByLocationIDAndDateAndTime?.nextToken;

      return { appointments: items, nextToken: newToken || null };
    } catch (err) {
      console.error(
        "Error fetching appointments for scheduler assistant by location:",
        err,
      );
      return { appointments: [], nextToken: null };
    }
  }
}

export const appointmentListService =
  AppointmentListService.getInstance(graphqlClient);

/****************************/
/***** Helper functions *****/
/****************************/

function getDateRangeFilter(startDate?: string, endDate?: string) {
  return {
    ...(startDate && { ge: startDate }),
    ...(endDate && { le: endDate }),
  };
}
